import { useInfiniteQuery } from 'react-query';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import type { ApiCollectionResponse } from 'types/api_response';

export type ManualUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgId: string;
};

export default function useIssuedManualUsers<T extends ManualUser>(
  manualId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexipub/manuals/issued/v1/manuals/${manualId}/permissions/users`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['issued_manual_users', manualId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
